import { buildDigitalTwinDataUrl, getDigitalTwinDataHeaders } from "../../api/digitalTwin/fieldDataClient";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MeasurementTrendCards from "../../components/digitalTwin/detail/measurementTrendCards.component";
import SingleInfoCard from "../../components/digitalTwin/detail/singleInfoCard.component";
import SuggestionCard from "../../components/digitalTwin/detail/suggestionCard.component";
import TimelineCard from "../../components/digitalTwin/detail/timelineCard.component";
import ThreeDCard from "../../components/digitalTwin/threeViewer/threeCard.component";
import {
  fetchLivestockDigitalTwinsStart,
  fetchLivestockMeasurementsStart,
} from "../../store/features/digitalTwin/farms/farmsSlice";
import {
  createDigitalTwinModelObjectUrlFromBase64,
  formatDigitalTwinFeatureLabel,
  formatDigitalTwinMeasurementValue,
  getDigitalTwinModelBase64Payload,
  getHoursSinceTimestamp,
  getLatest3DMeshDigitalTwin,
  getLatestMeasurementByName,
  getMeasurementTypes,
} from "../../utils/digitalTwinUtils";

const getLivestockId = (livestock) => livestock?.id || livestock?.livestock_id;

const getLivestockName = (livestock) =>
  livestock?.livestock_name ||
  livestock?.name ||
  livestock?.animal_type ||
  livestock?.livestock_type ||
  `Capo ${getLivestockId(livestock) ?? "-"}`;

const getApiList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const LivestockInfosCard = ({ livestock, measurements }) => {
  const measurementTypes = useMemo(
    () => getMeasurementTypes(measurements),
    [measurements],
  );

  const rows = useMemo(
    () => [
      [formatDigitalTwinFeatureLabel("name"), getLivestockName(livestock)],
      ...measurementTypes.map((measurement) => {
        const latestMeasurement = getLatestMeasurementByName(
          measurements,
          measurement.key,
        );

        return [
          measurement.label,
          formatDigitalTwinMeasurementValue(
            latestMeasurement?.measurement_value,
            measurement.key,
          ),
        ];
      }),
    ],
    [livestock, measurementTypes, measurements],
  );

  return (
    <Card
      variant="outlined"
      sx={{
        ml: 2,
        mr: 2,
        mt: 1,
        mb: 1,
        p: 2,
        backgroundColor: "background.default",
      }}
    >
      <CardContent>
        <Typography variant="body1" sx={{ fontWeight: "bold", pb: 2 }}>
          Caratteristiche
        </Typography>
        {rows.map(([label, value]) => (
          <Box
            key={label}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              py: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontWeight: 500 }}
            >
              {label}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.primary", fontWeight: 600 }}
            >
              {value}
            </Typography>
          </Box>
        ))}
      </CardContent>
    </Card>
  );
};

const LivestockDetailPage = () => {
  const [openTrendKey, setOpenTrendKey] = useState(null);
  const [modelUrl, setModelUrl] = useState(null);
  const [modelStatus, setModelStatus] = useState("idle");
  const [modelPayloadFormat, setModelPayloadFormat] = useState(null);
  const dispatch = useDispatch();
  const selectedLivestock = useSelector(
    (state) => state.selectedDigitalTwin.singleLivestock,
  );
  const measurements = getApiList(
    useSelector((state) => state.farms?.livestockMeasurements?.data),
  );
  const livestockDigitalTwins = getApiList(
    useSelector((state) => state.farms?.livestockDigitalTwins?.data),
  );

  const latest3DMeshDigitalTwin = useMemo(
    () => getLatest3DMeshDigitalTwin(livestockDigitalTwins),
    [livestockDigitalTwins],
  );
  const latest3DMeshUpdatedHoursAgo = useMemo(
    () => getHoursSinceTimestamp(latest3DMeshDigitalTwin?.survey_timestamp),
    [latest3DMeshDigitalTwin?.survey_timestamp],
  );

  useEffect(() => {
    const livestockId = getLivestockId(selectedLivestock);
    if (!livestockId) return;

    setOpenTrendKey(null);
    dispatch(fetchLivestockMeasurementsStart({ livestock_id: livestockId }));
    dispatch(fetchLivestockDigitalTwinsStart({ livestock_id: livestockId }));
  }, [dispatch, selectedLivestock]);

  useEffect(() => {
    const livestockId =
      latest3DMeshDigitalTwin?.livestock_id ||
      getLivestockId(selectedLivestock);

    if (
      !livestockId ||
      !latest3DMeshDigitalTwin?.survey_id ||
      !latest3DMeshDigitalTwin?.method_id
    ) {
      setModelUrl(null);
      setModelStatus("missing");
      setModelPayloadFormat(null);
      return undefined;
    }

    let isMounted = true;
    let objectUrlToRevoke = null;

    const loadDigitalTwinModel = async () => {
      try {
        setModelStatus("loading");
        setModelUrl(null);
        setModelPayloadFormat(null);

        const params = new URLSearchParams({
          livestock_id: String(livestockId),
          survey_id: String(latest3DMeshDigitalTwin.survey_id),
          method_id: String(latest3DMeshDigitalTwin.method_id),
        });
        const response = await fetch(
          buildDigitalTwinDataUrl(`livestock_digital_twin/?${params.toString()}`),
          {
            headers: getDigitalTwinDataHeaders(),
          },
        );

        if (!isMounted) return;

        if (!response.ok) {
          throw new Error(
            `Errore download modello 3D livestock: ${response.status}`,
          );
        }

        const jsonPayload = await response.json();
        const modelData = createDigitalTwinModelObjectUrlFromBase64(
          getDigitalTwinModelBase64Payload(jsonPayload),
          latest3DMeshDigitalTwin.model_file,
        );

        setModelPayloadFormat(modelData?.format || null);

        if (modelData && modelData.isCompatible === false) {
          console.warn(
            `Formato modello 3D livestock non compatibile con GLTFLoader: ${modelData.format}`,
          );
          setModelStatus("incompatible");
          return;
        }

        if (!modelData?.url || !modelData?.isCompatible) {
          throw new Error(
            "La risposta JSON del modello 3D livestock non contiene un model_base64 valido",
          );
        }

        objectUrlToRevoke = modelData.shouldRevoke ? modelData.url : null;
        setModelUrl(modelData.url);
        setModelStatus("ready");
      } catch (downloadError) {
        console.error(
          "Errore durante il fetch del modello 3D livestock:",
          downloadError,
        );
        if (isMounted) {
          setModelUrl(null);
          setModelStatus("error");
        }
      }
    };

    loadDigitalTwinModel();

    return () => {
      isMounted = false;
      if (objectUrlToRevoke) {
        window.URL.revokeObjectURL(objectUrlToRevoke);
      }
    };
  }, [latest3DMeshDigitalTwin, selectedLivestock]);

  const mockupInfos = [
    { time: "10:00", value: "Lorem ipsum dolor sit amet" },
    { time: "11:00", value: "Consectetur adipiscing elit" },
    {
      time: "12:00",
      value:
        "Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua",
    },
  ];

  return (
    <Box sx={{ p: 2, pt: 4 }}>
      <Grid container spacing={2}>
        <Grid item xs={8}>
          <ThreeDCard
            modelUrl={modelUrl}
            modelStatus={modelStatus}
            modelTimestamp={latest3DMeshDigitalTwin?.survey_timestamp}
            modelUpdatedHoursAgo={latest3DMeshUpdatedHoursAgo}
            modelPayloadFormat={modelPayloadFormat}
          />
         {/*  <TimelineCard title="Timeline Eventi" infos={mockupInfos} /> */}
          <MeasurementTrendCards
            measurements={measurements}
            openTrendKey={openTrendKey}
            onToggle={setOpenTrendKey}
          />
        </Grid>
        <Grid item xs={4}>
          {/* We provide some cards that are going to show important informations, like suggestions, warnings, */}
         {/*  <SuggestionCard
            title="Info"
            description="This is an info card"
            type="alert"
          /> */}
          <SingleInfoCard
            title="Stato salute"
            value="-"
            valueColor="#900B09"
            subtitle="Score generale dello stato di salute"
          />
          <LivestockInfosCard
            livestock={selectedLivestock}
            measurements={measurements}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default LivestockDetailPage;
